import React, { useMemo } from 'react';
import PropTypes from 'prop-types';

const SmallCalendar = ({ month = new Date().getMonth(), year = new Date().getFullYear() }) => {
  // Get current date to highlight today
  const today = new Date();
  const isCurrentMonth = today.getMonth() === month && today.getFullYear() === year;
  const currentDay = today.getDate();
  
  // Days of week header
  const daysOfWeek = ['M', 'Tu', 'W', 'Th', 'F', 'Sa', 'Su'];
  
  // Calendar data calculation
  const calendarData = useMemo(() => {
    const firstDayOfMonth = new Date(year, month, 1);
    const lastDayOfMonth = new Date(year, month + 1, 0);
    const daysInMonth = lastDayOfMonth.getDate();
    
    // Adjust for Monday as first day of week (0 is Monday, 6 is Sunday)
    let firstWeekday = firstDayOfMonth.getDay() - 1;
    if (firstWeekday === -1) firstWeekday = 6; // Sunday becomes 6
    
    // Days from previous month
    const daysFromPrevMonth = firstWeekday;
    const prevMonthDays = new Date(year, month, 0).getDate();
    
    const calendarDays = [];
    
    // Add days from previous month
    for (let i = 0; i < daysFromPrevMonth; i++) {
      calendarDays.push({
        date: prevMonthDays - daysFromPrevMonth + i + 1,
        isCurrentMonth: false,
        isPrevMonth: true
      });
    }
    
    // Add days from current month
    for (let i = 1; i <= daysInMonth; i++) {
      calendarDays.push({
        date: i,
        isCurrentMonth: true,
        isToday: isCurrentMonth && i === currentDay
      });
    }
    
    // Add days from next month
    const remainingDays = 35 - calendarDays.length; // 5 rows of 7 days
    for (let i = 1; i <= remainingDays; i++) {
      calendarDays.push({
        date: i,
        isCurrentMonth: false,
        isNextMonth: true
      });
    }
    
    // Group into weeks
    const weeks = [];
    for (let i = 0; i < calendarDays.length; i += 7) {
      weeks.push(calendarDays.slice(i, i + 7));
    }
    
    return weeks;
  }, [month, year, isCurrentMonth, currentDay]);

  return (
    <div style={{
      alignSelf: 'stretch', 
      height: 298, 
      padding: 10, 
      borderBottom: '1px #E7E7E7 solid', 
      flexDirection: 'column', 
      justifyContent: 'flex-start', 
      alignItems: 'flex-start', 
      gap: 10, 
      display: 'inline-flex'
    }}>
      <div style={{
        alignSelf: 'stretch', 
        flex: '1 1 0', 
        flexDirection: 'column', 
        justifyContent: 'flex-start', 
        alignItems: 'flex-start', 
        display: 'flex'
      }}>
        {/* Days of week header */}
        <div style={{
          alignSelf: 'stretch', 
          flex: '1 1 0', 
          opacity: 0.70, 
          justifyContent: 'space-between', 
          alignItems: 'flex-start', 
          display: 'inline-flex'
        }}>
          {daysOfWeek.map((day) => (
            <div key={day} style={{
              padding: 4, 
              justifyContent: 'flex-start', 
              alignItems: 'flex-start', 
              gap: 10, 
              display: 'flex'
            }}>
              <div style={{
                width: 26, 
                padding: 5, 
                justifyContent: 'center', 
                alignItems: 'center', 
                gap: 10, 
                display: 'flex'
              }}>
                <div style={{
                  justifyContent: 'center', 
                  display: 'flex', 
                  flexDirection: 'column', 
                  color: '#2E2E2E', 
                  fontSize: 14, 
                  fontFamily: 'Arial', 
                  fontWeight: '400', 
                  lineHeight: '24px', 
                  wordWrap: 'break-word'
                }}>
                  {day}
                </div>
              </div>
            </div>
          ))}
        </div>
        
        {/* Calendar weeks */}
        {calendarData.map((week, weekIndex) => (
          <div key={`week-${weekIndex}`} style={{
            alignSelf: 'stretch', 
            flex: '1 1 0', 
            justifyContent: 'space-between', 
            alignItems: 'flex-start', 
            display: 'inline-flex'
          }}>
            {week.map((day, dayIndex) => (
              <div key={`day-${weekIndex}-${dayIndex}`} style={{
                padding: 4, 
                justifyContent: 'flex-start', 
                alignItems: 'flex-start', 
                gap: 10, 
                display: 'flex',
                opacity: day.isCurrentMonth ? 1 : 0.3
              }}>
                {day.isToday ? (
                  <div style={{
                    padding: 4, 
                    background: '#0364F3', 
                    borderRadius: 99, 
                    justifyContent: 'center', 
                    alignItems: 'center', 
                    gap: 10, 
                    display: 'flex'
                  }}>
                    <div style={{
                      justifyContent: 'center', 
                      display: 'flex', 
                      flexDirection: 'column', 
                      color: 'white', 
                      fontSize: 14, 
                      fontFamily: 'Arial', 
                      fontWeight: '400', 
                      lineHeight: '24px', 
                      wordWrap: 'break-word'
                    }}>
                      {day.date.toString().padStart(2, '0')}
                    </div>
                  </div>
                ) : (
                  <div style={{
                    width: 22, 
                    padding: 5, 
                    justifyContent: 'flex-start', 
                    alignItems: 'flex-start', 
                    gap: 10, 
                    display: 'flex'
                  }}>
                    <div style={{
                      justifyContent: 'center', 
                      display: 'flex', 
                      flexDirection: 'column', 
                      color: '#151515', 
                      fontSize: 14, 
                      fontFamily: 'Arial', 
                      fontWeight: '400', 
                      lineHeight: '24px', 
                      wordWrap: 'break-word'
                    }}>
                      {day.date.toString().padStart(2, '0')}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

SmallCalendar.propTypes = {
  month: PropTypes.number,
  year: PropTypes.number
};

export default SmallCalendar;